"use client";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { RefObject } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

gsap.registerPlugin(ScrollTrigger);

type UseFadeInAnimationProps = {
  triggerRef?: RefObject<HTMLElement | null>;
  animateChildren?: boolean;
  animationProps?: gsap.TweenVars;
  /** Disable animations for reduced motion preference */
  respectReducedMotion?: boolean;
  /** Minimum percentage of element that must be visible before animation starts */
  visibilityThreshold?: number;
};

export function useFadeInAnimation<T extends HTMLElement>({
  triggerRef,
  animateChildren = false,
  animationProps = {},
  respectReducedMotion = true,
  visibilityThreshold = 0.15, // Default: 15% visible before animation starts
}: UseFadeInAnimationProps) {
  const elementRef = useRef<T>(null);

  // Check for reduced motion preference (SSR-safe)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPrefersReducedMotion(
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    }
  }, []);

  useLayoutEffect(() => {
    const triggerEl = triggerRef?.current || elementRef.current;
    if (!(triggerEl && elementRef.current)) return;

    // Skip animations if user prefers reduced motion
    if (respectReducedMotion && prefersReducedMotion) {
      gsap.set(elementRef.current, { autoAlpha: 1 });
      return;
    }

    const targets = animateChildren
      ? (elementRef.current.children as HTMLCollectionOf<HTMLElement>)
      : elementRef.current;

    // Create animation configuration
    const createAnimationVars = () => {
      // Calculate start position based on visibility threshold
      const startPosition = `top ${100 - visibilityThreshold * 100}%`;
      const endPosition = `bottom ${visibilityThreshold * 100}%`;

      const defaults: gsap.TweenVars = {
        autoAlpha: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: triggerEl,
          start: startPosition, // Dynamic start based on visibility threshold
          end: endPosition,
          toggleActions: "play none none reverse",
          // Ensure animation waits for element to be properly in view
          once: false, // Allow reverse animations
        },
      };

      const vars = { ...defaults, ...animationProps };

      if (animateChildren) {
        if (vars.stagger === undefined) vars.stagger = 0.08;
        if (vars.y === undefined) vars.y = 30;
      }

      return vars;
    };

    const ctx = gsap.context(() => {
      gsap.from(targets, createAnimationVars());
    }, triggerEl);

    return () => ctx.revert();
  }, [
    triggerRef,
    animateChildren,
    animationProps,
    respectReducedMotion,
    prefersReducedMotion,
    visibilityThreshold,
  ]);

  return elementRef;
}
